import { useMemo, useState } from 'react'
import { Button, Card, Meter, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { PageHeader } from '@/app/layouts/PageHeader'
import { formatMoney, fromMajor } from '@/lib/money'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from '@/features/transfers/SendAbroadScreen.module.css'

const PLATFORM_FEE = 0.01

export function ReturnsCalculatorScreen() {
  const [amount, setAmount] = useState('2500')
  const [years, setYears] = useState(5)
  const [rate, setRate] = useState(9.2)

  const model = useMemo(() => {
    const parsed = Number.parseFloat(amount.replace(/,/g, ''))
    const principal = Number.isFinite(parsed) && parsed > 0 ? parsed : 0
    const net = rate / 100 - PLATFORM_FEE
    const rentPerYear = principal * net
    const totalRent = rentPerYear * years
    const ending = principal + totalRent
    return {
      principal: fromMajor(principal, 'USD'),
      rentPerYear: fromMajor(rentPerYear, 'USD'),
      totalRent: fromMajor(totalRent, 'USD'),
      ending: fromMajor(ending, 'USD'),
      net,
    }
  }, [amount, years, rate])

  return (
    <>
      <PageHeader
        title="Returns calculator"
        subtitle="Rent only. It does not assume the building appreciates, because that is not a promise we can make."
        actions={<Button variant="ghost" size="md" to="/real-assets">All buildings</Button>}
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="light">
            <Stack gap={5}>
              <Stack gap={3}>
                <Text variant="caption" weight="semibold">How much do you put in?</Text>
                <div className={styles.amountField}>
                  <Text variant="eyebrow" tone="secondary">From your US Dollar account</Text>
                  <div className={styles.amountRow}>
                    <input
                      className={styles.amountInput}
                      inputMode="decimal"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value.replace(/[^\d.,]/g, ''))}
                      aria-label="Amount to invest"
                    />
                    <Text variant="subtitle" weight="semibold" tone="secondary">USD</Text>
                  </div>
                </div>
              </Stack>

              <Stack gap={3}>
                <Stack direction="row" justify="between" align="center">
                  <Text variant="caption" weight="semibold">For how long?</Text>
                  <Pill tone="neutral">{years} years</Pill>
                </Stack>
                <input
                  type="range"
                  min={1}
                  max={15}
                  value={years}
                  onChange={(event) => setYears(Number(event.target.value))}
                  aria-label="Holding period in years"
                  style={{ width: '100%', accentColor: 'var(--pb-ink)' }}
                />
              </Stack>

              <Stack gap={3}>
                <Stack direction="row" justify="between" align="center">
                  <Text variant="caption" weight="semibold">Target yield</Text>
                  <Pill tone="neutral">{rate.toFixed(1)}%</Pill>
                </Stack>
                <input
                  type="range"
                  min={6}
                  max={12}
                  step={0.1}
                  value={rate}
                  onChange={(event) => setRate(Number(event.target.value))}
                  aria-label="Target yield"
                  style={{ width: '100%', accentColor: 'var(--pb-ink)' }}
                />
                <Meter value={(rate - 6) / 6} height={6} tone="ink" label="Yield within the range on offer" />
              </Stack>
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="caption" weight="semibold" tone="onDark">After {years} years</Text>
              <Pill tone="accent">{(model.net * 100).toFixed(1)}% net</Pill>
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-4">
              {formatMoney(model.ending, { compactDecimals: true })}
            </Text>
            <div className={checkout.rule} />
            {[
              ['You put in', formatMoney(model.principal, { compactDecimals: true })],
              ['Rent per year', formatMoney(model.rentPerYear)],
              [`Rent over ${years} years`, formatMoney(model.totalRent, { compactDecimals: true })],
              ['Platform fee', '1.0% of rent'],
            ].map(([label, value]) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">{label}</Text>
                <Text variant="caption" weight="semibold" tone="onDark" numeric>{value}</Text>
              </div>
            ))}
            <Card.Footer>
              <Text variant="eyebrow" tone="onDarkMuted">
                Rent is paid monthly and can fall if the building sits empty. Nothing here is a
                guaranteed return, and you can lose what you put in.
              </Text>
            </Card.Footer>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
